// Extended Healthcare Knowledge Base
export const extendedHealthcareData = {
  // Medical Conditions Database
  conditions: {
    hypertension: {
      name: "High Blood Pressure (Hypertension)",
      symptoms: ["headaches", "dizziness", "shortness of breath", "nosebleeds"],
      causes: ["poor diet", "lack of exercise", "stress", "genetics", "smoking"],
      prevention: ["healthy diet", "regular exercise", "limit sodium", "manage stress", "avoid smoking"],
      info: "High blood pressure is often called the 'silent killer' because it typically has no symptoms until it becomes severe."
    },
    diabetes: {
      name: "Diabetes",
      symptoms: ["increased thirst", "frequent urination", "fatigue", "blurred vision", "slow healing"],
      causes: ["genetics", "obesity", "physical inactivity", "age", "poor diet"],
      prevention: ["healthy weight", "physical activity", "healthy diet", "regular checkups"],
      info: "Diabetes is a group of metabolic disorders characterized by high blood sugar levels."
    },
    anxiety: {
      name: "Anxiety",
      symptoms: ["excessive worry", "restlessness", "fatigue", "difficulty concentrating", "sleep problems"],
      causes: ["stress", "genetics", "brain chemistry", "personality", "life events"],
      prevention: ["stress management", "regular exercise", "adequate sleep", "social support"],
      info: "Anxiety disorders are the most common mental health conditions, affecting millions of people."
    },
    asthma: {
      name: "Asthma",
      symptoms: ["wheezing", "shortness of breath", "chest tightness", "coughing", "difficulty breathing"],
      causes: ["allergies", "genetics", "environmental factors", "respiratory infections"],
      prevention: ["avoid triggers", "maintain clean environment", "regular medication", "exercise safely"],
      info: "Asthma is a respiratory condition where airways narrow and swell, making breathing difficult."
    },
    arthritis: {
      name: "Arthritis",
      symptoms: ["joint pain", "stiffness", "swelling", "reduced range of motion", "tenderness"],
      causes: ["age", "genetics", "injury", "infection", "autoimmune factors"],
      prevention: ["maintain healthy weight", "regular exercise", "protect joints", "healthy diet"],
      info: "Arthritis involves inflammation of one or more joints, causing pain and stiffness."
    },
    depression: {
      name: "Depression",
      symptoms: ["persistent sadness", "loss of interest", "fatigue", "sleep changes", "appetite changes"],
      causes: ["brain chemistry", "genetics", "life events", "medical conditions", "medications"],
      prevention: ["social support", "regular exercise", "stress management", "healthy lifestyle"],
      info: "Depression is a mood disorder that causes persistent feelings of sadness and loss of interest."
    },
    migraine: {
      name: "Migraine",
      symptoms: ["severe headache", "nausea", "light sensitivity", "sound sensitivity", "visual disturbances"],
      causes: ["genetics", "hormones", "stress", "certain foods", "sleep changes"],
      prevention: ["identify triggers", "regular sleep", "stress management", "healthy diet"],
      info: "Migraines are severe headaches often accompanied by nausea and sensitivity to light and sound."
    },
    obesity: {
      name: "Obesity",
      symptoms: ["excess weight", "fatigue", "joint pain", "breathing problems", "sleep issues"],
      causes: ["poor diet", "lack of exercise", "genetics", "medical conditions", "medications"],
      prevention: ["balanced diet", "regular exercise", "portion control", "lifestyle changes"],
      info: "Obesity is a condition involving excessive body fat that increases health risks."
    },
    insomnia: {
      name: "Insomnia",
      symptoms: ["difficulty falling asleep", "frequent waking", "early morning awakening", "daytime fatigue"],
      causes: ["stress", "anxiety", "depression", "medical conditions", "medications", "poor sleep habits"],
      prevention: ["sleep hygiene", "regular schedule", "stress management", "comfortable environment"],
      info: "Insomnia is a sleep disorder that makes it hard to fall asleep or stay asleep."
    },
    osteoporosis: {
      name: "Osteoporosis",
      symptoms: ["bone fractures", "back pain", "loss of height", "stooped posture"],
      causes: ["age", "hormones", "low calcium", "lack of exercise", "genetics"],
      prevention: ["calcium and vitamin D", "weight-bearing exercise", "avoid smoking", "limit alcohol"],
      info: "Osteoporosis is a condition where bones become weak and brittle."
    },
    heart_disease: {
      name: "Heart Disease",
      symptoms: ["chest pain", "shortness of breath", "fatigue", "irregular heartbeat", "swollen legs"],
      causes: ["high blood pressure", "high cholesterol", "smoking", "diabetes", "obesity"],
      prevention: ["healthy diet", "regular exercise", "no smoking", "manage stress", "regular checkups"],
      info: "Heart disease refers to several conditions affecting the heart and blood vessels."
    },
    stroke: {
      name: "Stroke",
      symptoms: ["sudden numbness", "confusion", "trouble speaking", "severe headache", "vision problems"],
      causes: ["blood clots", "bleeding in brain", "high blood pressure", "diabetes", "smoking"],
      prevention: ["control blood pressure", "healthy diet", "exercise", "no smoking", "limit alcohol"],
      info: "A stroke occurs when blood supply to part of the brain is interrupted or reduced."
    },
    pneumonia: {
      name: "Pneumonia",
      symptoms: ["cough with phlegm", "fever", "chills", "shortness of breath", "chest pain"],
      causes: ["bacteria", "viruses", "fungi", "weakened immune system"],
      prevention: ["vaccination", "good hygiene", "healthy lifestyle", "avoid smoking"],
      info: "Pneumonia is an infection that inflames air sacs in one or both lungs."
    },
    bronchitis: {
      name: "Bronchitis",
      symptoms: ["persistent cough", "mucus production", "fatigue", "shortness of breath", "chest discomfort"],
      causes: ["viral infections", "bacterial infections", "smoking", "air pollution"],
      prevention: ["avoid smoking", "good hygiene", "avoid air pollutants", "vaccination"],
      info: "Bronchitis is inflammation of the bronchial tubes that carry air to your lungs."
    },
    gastritis: {
      name: "Gastritis",
      symptoms: ["stomach pain", "nausea", "vomiting", "bloating", "loss of appetite"],
      causes: ["bacterial infection", "excessive alcohol", "stress", "certain medications"],
      prevention: ["avoid irritants", "eat smaller meals", "manage stress", "limit alcohol"],
      info: "Gastritis is inflammation of the stomach lining."
    },
    kidney_stones: {
      name: "Kidney Stones",
      symptoms: ["severe pain", "blood in urine", "nausea", "frequent urination", "fever"],
      causes: ["dehydration", "diet", "genetics", "medical conditions"],
      prevention: ["stay hydrated", "limit sodium", "balanced diet", "maintain healthy weight"],
      info: "Kidney stones are hard deposits made of minerals and salts that form inside kidneys."
    },
    allergies: {
      name: "Allergies",
      symptoms: ["sneezing", "runny nose", "itchy eyes", "skin rash", "swelling"],
      causes: ["pollen", "dust mites", "pet dander", "certain foods", "medications"],
      prevention: ["avoid triggers", "keep environment clean", "allergy medications", "immunotherapy"],
      info: "Allergies occur when your immune system reacts to foreign substances."
    },
    dermatitis: {
      name: "Dermatitis",
      symptoms: ["red rash", "itchy skin", "dry skin", "blisters", "scaling"],
      causes: ["allergens", "irritants", "genetics", "stress"],
      prevention: ["moisturize skin", "avoid irritants", "gentle skincare", "manage stress"],
      info: "Dermatitis is inflammation of the skin that causes rashes and irritation."
    },

    eczema: {
      name: "Eczema",
      symptoms: ["itchy skin", "red patches", "dry skin", "scaling", "inflammation"],
      causes: ["genetics", "environmental factors", "immune system", "stress"],
      prevention: ["moisturize regularly", "avoid triggers", "gentle skincare", "manage stress"],
      info: "Eczema is a condition that makes skin red and itchy."
    },
    psoriasis: {
      name: "Psoriasis",
      symptoms: ["red patches", "silvery scales", "itching", "burning", "thick nails"],
      causes: ["immune system", "genetics", "stress", "infections", "medications"],
      prevention: ["manage stress", "avoid triggers", "moisturize", "healthy lifestyle"],
      info: "Psoriasis is a skin disease that causes red, itchy scaly patches."
    },
    fibromyalgia: {
      name: "Fibromyalgia",
      symptoms: ["widespread pain", "fatigue", "sleep problems", "memory issues", "mood problems"],
      causes: ["genetics", "infections", "physical trauma", "psychological stress"],
      prevention: ["regular exercise", "stress management", "good sleep habits", "healthy diet"],
      info: "Fibromyalgia is a disorder characterized by widespread musculoskeletal pain."
    }
    ,
    hypothyroidism: {
      name: "Hypothyroidism",
      symptoms: ["fatigue", "weight gain", "cold intolerance", "dry skin", "constipation"],
      causes: ["autoimmune disease", "iodine deficiency", "thyroid surgery", "medications"],
      prevention: ["adequate iodine intake", "regular checkups", "manage autoimmune conditions"],
      info: "Hypothyroidism is a condition where the thyroid gland doesn't produce enough hormones."
    },
    COPD: {
      name: "Chronic Obstructive Pulmonary Disease (COPD)",
      symptoms: ["chronic cough", "shortness of breath", "wheezing", "fatigue"],
      causes: ["smoking", "air pollution", "genetics", "respiratory infections"],
      prevention: ["no smoking", "avoid air pollutants", "vaccination", "regular exercise"],
      info: "COPD is a group of lung diseases that block airflow and make breathing difficult."
    },
    anemia: {
      name: "Anemia",
      symptoms: ["fatigue", "pale skin", "shortness of breath", "dizziness", "cold hands and feet"],
      causes: ["iron deficiency", "chronic disease", "blood loss", "genetics"],
      prevention: ["iron-rich diet", "treat underlying conditions", "regular checkups"],
      info: "Anemia is a condition in which you lack enough healthy red blood cells to carry oxygen."
    }
  },
   turboclin: {
    name: "Turboclin",
    symptoms: ["cough", "shortness of breath", "wheezing", "chest tightness"],
    causes: ["viral infections", "bacterial infections", "allergies", "environmental factors"],
    prevention: ["avoid allergens", "stay hydrated", "practice good hygiene", "manage stress"],
    info: "Turboclin is a respiratory condition characterized by inflammation of the airways."
  },
  // Body Systems Information
  bodySystems: {
    cardiovascular: {
      name: "Cardiovascular System",
      organs: ["heart", "blood vessels", "blood"],
      functions: ["pumps blood", "delivers oxygen", "removes waste", "maintains blood pressure"],
      healthTips: ["regular exercise", "heart-healthy diet", "limit alcohol", "don't smoke", "manage stress"]
    },
    respiratory: {
      name: "Respiratory System",
      organs: ["lungs", "trachea", "bronchi", "diaphragm"],
      functions: ["gas exchange", "oxygen delivery", "carbon dioxide removal", "breathing"],
      healthTips: ["avoid smoking", "exercise regularly", "good posture", "clean air", "deep breathing exercises"]
    },
    digestive: {
      name: "Digestive System",
      organs: ["stomach", "intestines", "liver", "pancreas"],
      functions: ["breaks down food", "absorbs nutrients", "eliminates waste"],
      healthTips: ["fiber-rich foods", "stay hydrated", "eat slowly", "limit processed foods", "probiotics"]
    }
    ,
    nervous: {
      name: "Nervous System",
      organs: ["brain", "spinal cord", "nerves"],
      functions: ["controls body functions", "processes information", "regulates emotions", "movement"],
      healthTips: ["mental stimulation", "adequate sleep", "stress management", "social interaction"]
    },
    musculoskeletal: {
      name: "Musculoskeletal System",
      organs: ["bones", "muscles", "joints", "tendons", "ligaments"],
      functions: ["supports body", "enables movement", "protects organs", "stores minerals"],
      healthTips: ["weight-bearing exercise", "calcium and vitamin D", "good posture", "stretching"]
    }
  },

  // Nutrition Information
  nutrition: {
    macronutrients: {
      carbohydrates: {
        function: "Primary energy source for the body",
        sources: ["whole grains", "fruits", "vegetables", "legumes"],
        recommended: "45-65% of total daily calories"
      },
      proteins: {
        function: "Building and repairing tissues, making enzymes and hormones",
        sources: ["lean meats", "fish", "eggs", "beans", "nuts"],
        recommended: "10-35% of total daily calories"
      },
      fats: {
        function: "Energy storage, vitamin absorption, hormone production",
        sources: ["olive oil", "avocados", "nuts", "fish", "seeds"],
        recommended: "20-35% of total daily calories"
      }
    },
    vitamins: {
      vitamin_c: {
        function: "Immune system support, collagen production",
        sources: ["citrus fruits", "berries", "bell peppers", "broccoli"],
        deficiency: ["scurvy", "poor wound healing", "frequent infections"]
      },
      vitamin_d: {
        function: "Bone health, immune function",
        sources: ["sunlight", "fatty fish", "fortified foods"],
        deficiency: ["weak bones", "muscle weakness", "increased infection risk"]
      },
      vitamin_b12: {
        function: "Nerve function, red blood cell formation",
        sources: ["meat", "fish", "dairy", "fortified foods"],
        deficiency: ["anemia", "fatigue", "nerve problems"]
      }
      ,
      vitamin_a: {
        function: "Vision, immune function, cell growth",
        sources: ["carrots", "sweet potatoes", "spinach", "eggs"],
        deficiency: ["night blindness", "dry skin", "increased infection risk"]
      },
      calcium: {
        function: "Bone and teeth health, muscle function",
        sources: ["dairy products", "leafy greens", "fortified foods"],
        deficiency: ["weak bones", "muscle cramps", "osteoporosis"]
      },
      iron: {
        function: "Red blood cell production, oxygen transport",
        sources: ["red meat", "beans", "spinach", "fortified cereals"],
        deficiency: ["anemia", "fatigue", "weakness"]
      }
    }
  },

  // Mental Health Resources
  mentalHealth: {
    strategies: {
      stress_management: [
        "Deep breathing exercises",
        "Progressive muscle relaxation",
        "Mindfulness meditation",
        "Regular physical activity",
        "Adequate sleep",
        "Social support",
        "Time management",
        "Hobbies and relaxation"
        ,
        "Nature walks",
        "Journaling"
      ],
      depression_support: [
        "Professional counseling",
        "Support groups",
        "Regular exercise",
        "Healthy diet",
        "Adequate sleep",
        "Social connections",
        "Mindfulness practices",
        "Medication when prescribed"
        ,
        "Creative activities (art, music)",
        "Gratitude journaling"
      ],
      anxiety_coping: [
        "Breathing techniques",
        "Grounding exercises",
        "Cognitive behavioral techniques",
        "Regular routine",
        "Limit caffeine",
        "Exercise regularly",
        "Seek professional help"
        ,
        "Visualization techniques",
        "Limit screen time"
      ]
    }
  },

  // Emergency Signs
  emergencySigns: {
    heart_attack: [
      "Chest pain or discomfort",
      "Pain in arms, neck, jaw, back",
      "Shortness of breath",
      "Nausea or vomiting",
      "Cold sweats",
      "Lightheadedness"
      ,
      "Unusual fatigue"
    ],
    stroke: [
      "Sudden numbness or weakness",
      "Confusion or trouble speaking",
      "Sudden trouble seeing",
      "Sudden severe headache",
      "Loss of balance or coordination"
      ,
      "Facial drooping (FAST test)"
    ],
    allergic_reaction: [
      "Difficulty breathing",
      "Swelling of face, lips, tongue",
      "Rapid pulse",
      "Dizziness or fainting",
      "Severe whole-body reaction"
      ,
      "Hives or rash"
    ]
    ,
    diabetic_emergency: [
      "Confusion or unconsciousness",
      "Rapid breathing",
      "Sweating",
      "Seizures",
      "Very high or low blood sugar"
    ]
  },

  // Preventive Care Guidelines
  preventiveCare: {
    screenings: {
      annual_physical: {
        frequency: "Yearly",
        includes: ["vital signs", "blood tests", "physical examination", "health history review"]
      },
      mammogram: {
        frequency: "Every 1-2 years after age 40",
        purpose: "Breast cancer screening"
      },
      colonoscopy: {
        frequency: "Every 10 years starting at age 45-50",
        purpose: "Colorectal cancer screening"
      },
      blood_pressure: {
        frequency: "At least every 2 years",
        purpose: "Hypertension screening"
      },
      eye_exam: {
        frequency: "Every 1-2 years",
        purpose: "Vision and eye health screening"
      },
      dental_checkup: {
        frequency: "Every 6-12 months",
        purpose: "Oral health screening"
      }
    },
    vaccinations: {
      flu_shot: {
        frequency: "Annual",
        purpose: "Influenza prevention"
      },
      covid_vaccine: {
        frequency: "As recommended by health authorities",
        purpose: "COVID-19 prevention"
      },
      tetanus: {
        frequency: "Every 10 years",
        purpose: "Tetanus prevention"
      }
    }
  },

  // Healthy Lifestyle Tips
  lifestyle: {
    exercise: {
      aerobic: {
        examples: ["walking", "running", "swimming", "cycling"],
        benefits: ["heart health", "weight management", "mood improvement"],
        recommendation: "150 minutes moderate intensity per week"
      },
      strength: {
        examples: ["weight lifting", "resistance bands", "bodyweight exercises"],
        benefits: ["muscle strength", "bone health", "metabolism"],
        recommendation: "2 or more days per week"
      },
      flexibility: {
        examples: ["stretching", "yoga", "tai chi"],
        benefits: ["range of motion", "injury prevention", "stress relief"],
        recommendation: "Daily stretching"
      },
      balance: {
        examples: ["balance board", "single-leg exercises", "tai chi"],
        benefits: ["fall prevention", "core strength", "coordination"],
        recommendation: "Include balance exercises 2-3 times per week"
      }
    },
    sleep: {
      recommendations: [
        "7-9 hours per night for adults",
        "Consistent sleep schedule",
        "Cool, dark, quiet environment",
        "Avoid screens before bedtime",
        "No caffeine 6 hours before sleep",
        "Regular bedtime routine"
        ,
        "Limit naps to 20-30 minutes",
        "Get morning sunlight exposure"
      ]
    },
    hydration: {
      recommendations: [
        "Drink water throughout the day",
        "Limit sugary drinks",
        "Increase fluids in hot weather or during exercise",
        "Monitor urine color for hydration"
      ]
    },
    nutrition: {
      recommendations: [
        "Eat a variety of fruits and vegetables",
        "Choose whole grains",
        "Limit processed foods and added sugars",
        "Include healthy fats",
        "Watch portion sizes",
        "Plan meals ahead"
      ]
    }
  }
}

// Health Risk Assessment Questions
export const healthAssessment = {
  lifestyle: [
    "Do you exercise regularly?",
    "Do you eat a balanced diet?",
    "Do you smoke or use tobacco?",
    "How much alcohol do you consume?",
    "How would you rate your stress level?",
    "How many hours of sleep do you get?"
  ],
  family_history: [
    "Any family history of heart disease?",
    "Any family history of diabetes?",
    "Any family history of cancer?",
    "Any family history of mental health conditions?"
  ],
  current_health: [
    "Any current medications?",
    "Any known allergies?",
    "Any chronic conditions?",
    "When was your last checkup?"
  ]
};

// Health Calculators
export const healthCalculators = {
  bmi: {
    formula: "weight (kg) / height (m)²",
    categories: {
      underweight: "< 18.5",
      normal: "18.5 - 24.9",
      overweight: "25 - 29.9",
      obese: "≥ 30"
    }
  },
  water_intake: {
    formula: "30-35 ml per kg of body weight",
    factors: ["activity level", "climate", "overall health"]
  },
  bmr: {
    formula: "10 x weight (kg) + 6.25 x height (cm) - 5 x age (years) + 5 (male) or -161 (female)",
    description: "Basal Metabolic Rate estimates daily calorie needs at rest."
  },
  ideal_weight: {
    formula: "50 kg + 0.91 x (height in cm - 152.4) for males; 45.5 kg + 0.91 x (height in cm - 152.4) for females",
    description: "Ideal body weight based on height and gender."
  }
};
